// OfficePage — 오피스(새 문서 런처): NC Direct Editing으로 새 문서 생성→Collabora 편집.
import { useState, useEffect } from 'react';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import { Page, PageHeader, Button } from '../components/ui';
import { officeAPI } from '../api/client';
import { IconFileText, IconPresentation, IconTable } from '@tabler/icons-react';
import { ensureNcSession, getGatewayUserId } from '../lib/ncSso';
import { causeText, classifyEmbedError, relogin } from '../lib/embedAuth';
import { card as cardBase, TONES, ICON_STROKE } from '../utils/uiStyles';
import { openInNewProcess } from '../utils/openNoopener';

// 오피스(Collabora) "새 문서 런처" — richdocuments는 독립 브라우징 페이지가 없어(/index=CSRF),
// NC Direct Editing API로 새 문서를 만들고 그 direct-edit URL 을 새 탭(openInNewProcess)으로 연다 —
// 런처는 그대로 두고 «열기» 줄을 남긴다(10-06 마스터 «새창으로 띄운거야»).
// 설계 = 루트 office_new_doc_launcher.md (C). 인증 = 같은 오리진 NC 세션 쿠키 + requesttoken(EmbedPage 패턴).
// fallback 확장자 = 능력조회 실패 시만 사용. 이 NC는 doc_format=ooxml(docx/xlsx/pptx).
// tone = 카드 아이콘의 파일 종류별 색(TONES 키 · 시안 F: 문서 파랑 · 스프레드시트 녹 · 프레젠테이션 주황).
const TYPES = [
  { key: 'document',     Icon: IconFileText,     label: '새 문서',          noun: '문서',          fallbackExt: 'docx', tone: 'blue' },
  { key: 'spreadsheet',  Icon: IconTable,        label: '새 스프레드시트',  noun: '스프레드시트',  fallbackExt: 'xlsx', tone: 'green' },
  { key: 'presentation', Icon: IconPresentation, label: '새 프레젠테이션',  noun: '프레젠테이션',  fallbackExt: 'pptx', tone: 'orange' },
];
// 문서 저장 폴더 = ASCII "Office". ⚠️한글 폴더명("오피스")은 NC userFolder->nodeExists의
// 유니코드 정규화(NFC/NFD) 불일치로 create가 'Invalid path'(부모 미존재 오판) → ASCII로 회피(검증완료).
const OFFICE_DIR = 'Office';

const ocsHeaders = { 'OCS-APIRequest': 'true' };

// NC CSRF requesttoken — EmbedPage와 동일하게 /drive/ HTML에서 추출
async function getRequestToken() {
  try {
    const html = await (await fetch('/drive/', { credentials: 'include' })).text();
    const m = html.match(/data-requesttoken="([^"]+)"/);
    return m ? m[1] : null;
  } catch (e) { return null; }
}

function pad(n) { return String(n).padStart(2, '0'); }
function stamp() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}${pad(d.getMinutes())}`;
}

export default function OfficePage() {
  const [uid, setUid] = useState(null);        // NC 세션 사용자 id(있으면 준비됨)
  const [booting, setBooting] = useState(true); // 세션 부트스트랩 중
  const [busy, setBusy] = useState('');          // 생성 중 유형 key
  const [err, setErr] = useState('');
  const [readyUrl, setReadyUrl] = useState('');    // 만든 문서의 편집기 URL — «[열기]» 줄(자동으로 못 열었거나 열렸는지 모를 때)
  const [readyNote, setReadyNote] = useState('');  // 그 줄의 문구
  const [bootFail, setBootFail] = useState(false);  // 부트스트랩 타임아웃 시 가시적 폴백
  const [bootCause, setBootCause] = useState(null); // 그 원인(lib/embedAuth CAUSE_TEXT 키) — 카드의 원인 줄
  const [bootAttempt, setBootAttempt] = useState(0);

  // 마운트 시 NC 세션을 single-flight 헬퍼로 확보한다.
  useEffect(() => {
    let alive = true;
    (async () => {
      setBooting(true);
      setBootFail(false);
      setBootCause(null);
      setErr('');
      try {
        const session = await ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: '/drive/apps/files/' });
        if (!alive) return;
        setUid(session.uid);
        setBooting(false);
      } catch (e) {
        if (!alive) return;
        setUid(null);
        setBootCause(classifyEmbedError(e));
        setBootFail(true);
        setBooting(false);
      }
    })();
    return () => { alive = false; };
  }, [bootAttempt]);

  // 페이지 진입 시 빈 문서 정리(편집 안 하고 30분 지난 런처 생성분). 서버가 nc_app_pw로 처리=세션 무관, 베스트에포트.
  useEffect(() => { officeAPI.cleanup().catch(() => {}); }, []);

  const retryConnection = () => setBootAttempt((n) => n + 1);

  const createDoc = async (type) => {
    setErr(''); setReadyUrl(''); setBusy(type.key);   // 이전 문서의 «[열기]» 줄을 남기지 않는다
    // 🔴 빈 창을 미리 열지 않는다 — 핸들을 쥔 창(opener 있음)은 게이트웨이와 같은 브라우저 프로세스에 묶인다(10-06).
    //    편집기는 URL 을 얻은 뒤 noopener 링크로 연다(아래).
    try {
      const session = await ensureNcSession({ expectedUid: getGatewayUserId(), redirectUrl: '/drive/apps/files/' });
      const id = session && session.uid;
      if (id && id !== uid) setUid(id);
      if (!id) { setErr('드라이브 세션이 아직 준비되지 않았습니다. 잠시 후 다시 시도해 주세요.'); return; }
      const token = await getRequestToken();
      // 능력조회 → creatorId별 확장자(doc_format 설정 반영). 실패 시 fallback 확장자.
      let ext = type.fallbackExt;
      try {
        const cj = await (await fetch('/drive/ocs/v2.php/apps/files/api/v1/directEditing?format=json', { headers: ocsHeaders, credentials: 'include' })).json();
        // creators는 배열이 아니라 dict: { document:{extension:'docx'}, spreadsheet:{...}, ... }
        const creators = (cj && cj.ocs && cj.ocs.data && cj.ocs.data.creators) || {};
        const c = creators[type.key];
        if (c && c.extension) ext = c.extension;
      } catch (e) { /* fallback 확장자 사용 */ }
      const fname = `새 ${type.noun} ${stamp()}.${ext}`;
      // 주어진 path로 새 문서 생성 시도 → 성공 시 direct-edit URL 반환, 실패 시 null
      const tryCreate = async (path) => {
        const body = new URLSearchParams({ path, editorId: 'richdocuments', creatorId: type.key });
        const res = await fetch('/drive/ocs/v2.php/apps/files/api/v1/directEditing/create?format=json', {
          method: 'POST', credentials: 'include',
          headers: { ...ocsHeaders, requesttoken: token || '', 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
        const j = await res.json().catch(() => null);
        return (j && j.ocs && j.ocs.data && j.ocs.data.url) || null;
      };
      // 대상 폴더 Office/ 보장 — WebDAV 쓰기는 requesttoken(CSRF) 필요. 실패해도 무시(아래 루트 폴백).
      try {
        await fetch(`/drive/remote.php/dav/files/${encodeURIComponent(id)}/${OFFICE_DIR}/`,
          { method: 'MKCOL', credentials: 'include', headers: { requesttoken: token || '' } });
      } catch (e) { /* 폴더 생성 실패 시 루트 폴백 */ }
      // 1) Office 폴더에 생성 → 2) 실패 시 루트에 생성(루트는 항상 존재=확실히 동작)
      let createdPath = `/${OFFICE_DIR}/${fname}`;
      let url = await tryCreate(createdPath);
      if (!url) { createdPath = `/${fname}`; url = await tryCreate(createdPath); }
      if (!url) throw new Error('생성 응답에 url 없음');
      // 빈 문서 자동삭제 추적: 생성한 빈 템플릿을 서버에 등록(편집 안 하고 30분 지나면 정리). 베스트에포트.
      officeAPI.track({ path: createdPath, ext }).catch(() => {});
      // 새 탭에서 편집(런처는 유지). await 가 여러 번이라 클릭의 사용자 활성화가 남았다는 보장이 없다 —
      // 남아 있으면 바로 noopener 링크로 열고, 아니면 «[열기]» 를 한 번 더 누르게 한다(조용히 사라지지 않게).
      // 자동으로 연 경우에도 줄을 남긴다 — 사파리·파이어폭스의 팝업 판정이 isActive 와 어긋나 막힐 수 있다.
      if (navigator.userActivation?.isActive === true && openInNewProcess(url)) {
        setReadyNote('새 탭이 안 열렸으면');
      } else {
        setReadyNote('새 ' + type.noun + '이(가) 준비됐습니다.');
      }
      setReadyUrl(url);
    } catch (e) {
      // 드라이브 세션(NcSsoError) 때문이면 원인 한 줄을 뒤에 붙인다.
      const why = e && e.name === 'NcSsoError' ? ' ' + causeText(classifyEmbedError(e)) : '';
      setErr('새 ' + type.noun + '을(를) 만들지 못했습니다. 잠시 후 다시 시도하거나, 드라이브에서 직접 문서를 열어 주세요.' + why);
    } finally {
      setBusy('');
    }
  };

  const openReady = () => { openInNewProcess(readyUrl); setReadyUrl(''); };   // 이 클릭이 사용자 활성화다

  return (
    <AppShell app="office">
      <div style={{ flex: 1, minWidth: 0, background: 'var(--gw-bg, #fff)', display: 'flex', flexDirection: 'column' }}>
        <Announcements scope="office" />
        {(booting || bootFail) ? (
          // 세션 부트스트랩 — single-flight SSO 헬퍼가 중복 개시와 무한 스피너를 막는다.
          <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', color: 'var(--gw-muted, #92918d)', fontFamily: 'var(--gw-font)', textAlign: 'center', padding: '24px', background: 'var(--gw-bg, #fff)' }}>
            {bootFail ? (
              <div style={{ ...cardBase, width: 'min(460px, 100%)', padding: '22px', boxSizing: 'border-box' }}>
                <div style={{ color: 'var(--gw-text, #32312e)', fontSize: '15px', fontWeight: 620 }}>드라이브 연결에 실패했습니다</div>
                <div style={{ fontSize: '13px', lineHeight: 1.6, color: 'var(--gw-secondary, #64635f)', marginTop: '8px' }}>잠시 후 다시 시도해 주세요.</div>
                <div data-gw-embed-auth-cause={bootCause || 'unknown'} style={{ fontSize: '12.5px', lineHeight: 1.6, color: 'var(--gw-red-ink, #a4564d)', marginTop: '6px' }}>{causeText(bootCause)}</div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '16px' }}>
                  <Button variant="primary" onClick={retryConnection}>다시 시도</Button>
                  <Button variant="secondary" onClick={() => { relogin(); }}>다시 로그인</Button>
                </div>
              </div>
            ) : (
              <div style={{ ...cardBase, width: 'min(360px, 100%)', padding: '22px', boxSizing: 'border-box', color: 'var(--gw-secondary, #64635f)' }}>오피스 준비 중…</div>
            )}
          </div>
        ) : (
          // 런처(새 문서 버튼) — 본문 틀 .gw-page · 머리 = 공용 PageHeader(아이콘·톤 = 사이드바 «오피스»)
          <div style={{ flex: 1, overflowY: 'auto', background: 'var(--gw-bg, #fff)', fontFamily: 'var(--gw-font)' }}>
            <Page>
              <PageHeader icon={IconFileText} tone="green" title="오피스 — 새 문서 만들기" subtitle={'새 문서는 드라이브의 "Office" 폴더에 저장됩니다.'} />
              {/* 유형 카드가 셋뿐이라 폭 880 유지 · 머리와 같은 왼쪽 선. 유형 단추는 grid 의 직계 자식(시험이 parentElement 를 카드 묶음으로 본다). */}
              <div style={{ maxWidth: '880px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                {TYPES.map((t) => {
                  const TypeIcon = t.Icon;
                  return (
                  <button key={t.key} onClick={() => createDoc(t)} disabled={!!busy}
                    style={{ ...cardBase, minHeight: '124px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', padding: '18px',
                      cursor: busy ? 'default' : 'pointer', opacity: busy && busy !== t.key ? 0.5 : 1, textAlign: 'left', color: 'inherit' }}>
                    {/* tabler 는 color 를 svg stroke 속성에 넣는다 → var() 가 아니라 TONES hex 를 넘긴다 */}
                    <TypeIcon size={34} stroke={ICON_STROKE} color={TONES[t.tone].ink} aria-hidden="true" />
                    <span style={{ fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' }}>{busy === t.key ? '만드는 중…' : t.label}</span>
                    <span style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)' }}>{t.fallbackExt.toUpperCase()}</span>
                  </button>
                  );
                })}
                </div>
                {readyUrl && (
                  <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px', color: 'var(--gw-text, #32312e)', background: 'var(--gw-subtle, #fbfbfa)', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '8px', padding: '10px 14px', marginTop: '16px', lineHeight: 1.6 }}>
                    <span>{readyNote}</span>
                    <Button variant="primary" onClick={openReady}>열기</Button>
                  </div>
                )}
                {err && (
                  <div style={{ fontSize: '13px', color: 'var(--gw-red-ink, #a4564d)', background: 'var(--gw-red-soft, #f8e4e0)', border: '1px solid var(--gw-red-soft, #f8e4e0)', borderRadius: '8px', padding: '12px 14px', marginTop: '16px', lineHeight: 1.6 }}>
                    {err} <a href="/drive-app" style={{ color: 'var(--gw-blue, #2383e2)', fontWeight: 600 }}>드라이브 열기</a>
                  </div>
                )}
                <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', marginTop: '12px' }}>문서는 드라이브에서 다시 열 수 있어요.</div>
              </div>
            </Page>
          </div>
        )}
      </div>
    </AppShell>
  );
}
